import { Action, combineReducers, createReducer, on } from '@ngrx/store'
import { getTodayDateTime } from '../../utils/credit.utils'
import {
  ClientSearch,
  CreditSubmissionStructure,
  CreditSubmissionReinsurer,
  CreditSubmissionUserTranche,
  CreditSubmissionReinsurerStatus,
} from '../../model/credit-submission.model'
import { CreditCalculationStructureActions } from '../structure/credit-structure.actions'
import { CreditTransactionActions } from '../transaction/credit-transaction.actions'
import {
  CreditSubmissionReinsurerActions,
  CreditSubmissionStatusActions,
  CreditSubmissionStructureActions,
  CreditSubmissionUserTrancheActions,
} from './credit-submission.actions'
import { SetModuleContext } from '../credit.actions'
import { CreditModuleContext } from '../../model/credit-routes.model'

export interface State {
  currentCreditSubmissionStructure?: CreditSubmissionStructure
  currentCreditSubmissionUserTranches?: CreditSubmissionUserTranche[]
  isDirty: boolean
  isSaving: boolean
  trancheSessionModifiedDate?: string
  searchResults?: CreditSubmissionStructure[]
  gseSearchResults?: CreditSubmissionStructure[]
  searchFilters?: ClientSearch
}

export const initialState: State = {
  isDirty: false,
  isSaving: false,
}

const currentCreditSubmissionStructureReducer = createReducer(
  initialState.currentCreditSubmissionStructure,
  on(
    CreditSubmissionStructureActions.fetchSuccess,
    CreditSubmissionStructureActions.setSelectedSubmission,
    CreditSubmissionStructureActions.fetchReinsurerAndUserIdsSuccess,
    CreditSubmissionStructureActions.updateSelectedSubmission,
    CreditSubmissionStatusActions.finalizeSuccess,
    CreditSubmissionStatusActions.sendForApprovalSuccess,
    CreditSubmissionStatusActions.sendBackSuccess,
    CreditSubmissionStatusActions.approveSuccess,
    CreditSubmissionStatusActions.sendToMarketSuccess,
    (_, { structure }) => structure,
  ),

  on(CreditSubmissionStructureActions.resetSelectedSubmission, (_) => undefined),
)

const currentCreditSubmissionUserTranchesReducer = createReducer(
  initialState.currentCreditSubmissionUserTranches,
  on(
    CreditSubmissionUserTrancheActions.fetchSuccess,
    CreditSubmissionUserTrancheActions.saveSuccess,
    (_, { tranches }) => tranches,
  ),
  on(CreditSubmissionUserTrancheActions.update, (state, { tranche }) =>
    state.map((s) => (s.tranche_id === tranche.tranche_id ? tranche : s)),
  ),
  on(CreditSubmissionStructureActions.resetSelectedSubmission, (_) => undefined),
)

const isDirtyReducer = createReducer(
  initialState.isDirty,
  on(
    CreditCalculationStructureActions.update,
    CreditSubmissionStructureActions.updateSelectedSubmission,
    CreditTransactionActions.updateSuccess,
    CreditSubmissionUserTrancheActions.update,
    (_) => true,
  ),
  on(
    CreditCalculationStructureActions.putSuccess,
    CreditSubmissionStructureActions.putSuccess,
    CreditSubmissionUserTrancheActions.saveSuccess,
    CreditSubmissionStructureActions.resetSelectedSubmission,
    (_) => false,
  ),
)

const isSavingReducer = createReducer(
  initialState.isSaving,
  on(CreditCalculationStructureActions.save, (isSaving, { moduleContext }) =>
    moduleContext === CreditModuleContext.Submission ? true : isSaving,
  ),
  on(CreditSubmissionUserTrancheActions.save, (_) => true),
  on(
    CreditCalculationStructureActions.putSuccess,
    CreditCalculationStructureActions.putFailure,
    CreditSubmissionStructureActions.putSuccess,
    CreditSubmissionStructureActions.putFailure,
    CreditSubmissionUserTrancheActions.saveSuccess,
    CreditSubmissionUserTrancheActions.saveFailure,
    CreditSubmissionStructureActions.resetSelectedSubmission,
    (_) => false,
  ),
)

const trancheSessionModifiedDateReducer = createReducer(
  initialState.trancheSessionModifiedDate,
  on(CreditSubmissionUserTrancheActions.saveSuccess, (_) => getTodayDateTime()),
)

const searchResultsReducer = createReducer(
  initialState.searchResults,
  on(
    CreditSubmissionStructureActions.getSubmissionStructuresSuccess,
    (_, { structures }) => structures,
  ),
  on(CreditSubmissionStructureActions.getSubmissionStructuresFailure, (_) => undefined),
  on(CreditSubmissionStructureActions.setSelectedFilters, (_) => undefined),
  on(SetModuleContext, (_) => undefined),
)

const searchFiltersReducer = createReducer(
  initialState.searchFilters,
  on(CreditSubmissionStructureActions.setSelectedFilters, (_, { filters }) => filters),
  on(CreditSubmissionStructureActions.setGseSelectedFilters, (_, { filters }) => filters),
)

const gseSearchResultsReducer = createReducer(
  initialState.gseSearchResults,
  on(
    CreditSubmissionStructureActions.getGseSubmissionStructuresSuccess,
    (_, { structures }) => structures,
  ),
  on(CreditSubmissionStructureActions.getSubmissionStructuresFailure, (_) => undefined),
  on(CreditSubmissionStructureActions.setGseSelectedFilters, (_) => undefined),
  on(SetModuleContext, (_) => undefined),
)

const submissionReducer = combineReducers<State>({
  currentCreditSubmissionStructure: currentCreditSubmissionStructureReducer,
  currentCreditSubmissionUserTranches: currentCreditSubmissionUserTranchesReducer,
  isDirty: isDirtyReducer,
  isSaving: isSavingReducer,
  trancheSessionModifiedDate: trancheSessionModifiedDateReducer,
  searchResults: searchResultsReducer,
  gseSearchResults: gseSearchResultsReducer,
  searchFilters: searchFiltersReducer,
})

export function reducer(state: State | undefined, action: Action) {
  return submissionReducer(state, action)
}
